import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'Who is behind the Qtap project?',
  answer: <>
        Qtap was developed by STDI Global, a technology innovation company led
        by Antonio Cioffi. STDI specializes in merging physical products with
        digital experiences. Learn more about the team and vision at{' '}
        <a href="https://stdiglobal.com/antonio-cioffi/" target="_blank" rel="noopener noreferrer" className="text-brand-cyan hover:text-brand-sky underline underline-offset-2 transition-colors" data-id="element-176">
          STDI Global
        </a>
        .
      </>
}, {
  question: 'Is the NFC technology patented?',
  answer: <>
        Yes. Qtap's NFC-integrated smart fabric technology is 100% U.S.
        patented. This proprietary innovation is legally protected — making Qtap
        the only authentic, officially recognized smart t-shirt on the market.
        Read more about our patent and technology at{' '}
        <a href="https://stdiglobal.com/antonio-cioffi/" target="_blank" rel="noopener noreferrer" className="text-brand-cyan hover:text-brand-sky underline underline-offset-2 transition-colors" data-id="element-177">
          STDI Global
        </a>
        .
      </>
}, {
  question: 'How does the NFC chip work?',
  answer: 'The NFC chip is seamlessly embedded into the t-shirt fabric. When someone holds any NFC-enabled smartphone near the chip, it instantly opens your dynamic digital profile — no app download required.'
}, {
  question: 'Does my t-shirt update in real time?',
  answer: 'Yes! Your Qtap smart t-shirt updates automatically in real time. Team news, live match stats, scores, and exclusive content are refreshed instantly — you never have to do anything. Plus, you can customize your digital profile anytime: change your social links, bio, or exclusive content whenever you want. Your t-shirt stays the same, but what it shares is always up to date.'
}, {
  question: 'How many smart t-shirts are available?',
  answer: "The first drop is limited to 5,000 units across all 32+ national team designs. Once they sell out, there's no guarantee of a restock. Bundle deals are available for those who want to gear up their whole crew."
}, {
  question: 'Do you ship internationally?',
  answer: "Yes! We offer free global shipping on all orders. Whether you're in the US, Europe, Latin America, or anywhere else — your Qtap smart t-shirt will arrive at your door."
}, {
  question: 'Is the smart t-shirt waterproof?',
  answer: 'Yes — 100% waterproof. Thanks to our patented NFC-integrated smart fabric technology, the embedded chip and all electronics are fully sealed and protected. Rain, sweat, spills, or even a full machine wash — your Qtap smart t-shirt keeps working flawlessly every time.'
}];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section id="faq" className="py-10 md:py-20 bg-brand-dark relative" data-id="element-178">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-179">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16" data-id="element-180">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-brand-cyan font-bold tracking-widest uppercase text-xs sm:text-sm mb-3 block" data-id="element-181">
            Got Questions?
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-white" data-id="element-182">
            Frequently Asked Questions
          </motion.h2>
        </div>

        {/* FAQ Accordion */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="glass-card rounded-2xl overflow-hidden" data-id="element-183">
          <div className="divide-y divide-white/5" data-id="element-184">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <div key={index} className="group" data-id="element-185">
                  <button onClick={() => toggleFAQ(index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left focus:outline-none hover:bg-white/[0.02] transition-colors" aria-expanded={isOpen} data-id="element-186">
                    <span className="font-bold text-base md:text-lg text-gray-200 group-hover:text-white transition-colors pr-4" data-id="element-187">
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-brand-cyan text-brand-darkest rotate-180' : 'bg-white/5 text-gray-400 group-hover:bg-white/10 group-hover:text-white'}`} data-id="element-188">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-189" />
                    </div>
                  </button>

                  <AnimatePresence initial={false} data-id="element-190">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-191">
                        <div className="p-5 md:p-6 pt-0 text-sm md:text-base text-gray-400 leading-relaxed border-t border-transparent" data-id="element-192">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}